import { fg } from '@atlaskit/platform-feature-flags';
import { getStudioSessionSyncUrl } from '@atlassian/studio-entry-link';

import { isStaging } from '../env-helpers/index.ts';

const STUDIO_PROD_URL = 'https://studio.atlassian.com';
const STUDIO_STAGING_URL = 'https://atlassian-studio.stg-east.frontend.public.atl-paas.net';

export const getStudioHost = (): string => {
	return isStaging() ? STUDIO_STAGING_URL : STUDIO_PROD_URL;
};

const getStudioEnv = (): 'production' | 'staging' => (isStaging() ? 'staging' : 'production');

export const getStudioPath = (path: string, email?: string): string => {
	if (email) {
		return getStudioSessionSyncUrl(getStudioEnv(), path, email);
	}

	return `${getStudioHost()}${path}`;
};

const buildStudioAutomationRuleEditUrl = (
	workspaceId: string,
	spaceId: string,
	ruleIdUuid: string,
): string => {
	const base = `/w/${workspaceId}/spaces/${spaceId}/automation#`;
	return `${base}/rule/${encodeURIComponent(ruleIdUuid)}`;
};

export const getAtlassianStudioNLCreateUrl = (
	siteId: string,
	navigationSource?: string,
	csid?: string | null,
	email?: string,
	starterText?: string,
): string => {
	const searchParams = new URLSearchParams();

	if (navigationSource) {
		searchParams.set('navigationSource', navigationSource);
	}
	if (csid && fg('rovo-agents-universal-analytics')) {
		searchParams.set('csid', csid);
	}
	if (starterText) {
		searchParams.set('starterText', starterText);
	}

	return getStudioPath(
		`/s/${siteId}/agents/create/chat${searchParams.size > 0 ? `?${searchParams}` : ''}`,
		email,
	);
};

export const getAtlassianStudioAgentCreateUrl = (siteId: string, email?: string): string => {
	return getStudioPath(`/s/${siteId}/agents/create/overview`, email);
};

export const getAtlassianStudioRedirectBaseUrl = (
	siteId: string,
	agentId: string,
	email?: string,
): string => getStudioPath(`/s/${siteId}/agents/enrich/rovo/agents/${agentId}`, email);

/**
 * @deprecated Use {@link getAtlassianStudioAgentEditUrlWithAri} with an agent ARI.
 * This legacy helper builds the enrich/rovo overview redirect URL from a bare agent id.
 */
export const getAtlassianStudioAgentEditUrl = (
	siteId: string,
	agentId: string,
	navigationSource?: string,
	email?: string,
): string => {
	const redirectParam = `redirect=${encodeURIComponent('/:agentId/overview')}`;
	const sourceParam = navigationSource
		? `&navigationSource=${encodeURIComponent(navigationSource)}`
		: '';
	return getStudioPath(
		`/s/${siteId}/agents/enrich/rovo/agents/${agentId}?${redirectParam}${sourceParam}`,
		email,
	);
};

/**
 * Builds the Studio agent details URL using the full agent ARI.
 */
export const getAtlassianStudioAgentEditUrlWithAri = (
	siteId: string,
	agentAri: string,
	navigationSource?: string,
	email?: string,
): string => {
	const sourceParam = navigationSource
		? `?navigationSource=${encodeURIComponent(navigationSource)}`
		: '';
	return getStudioPath(
		`/s/${siteId}/agents/${encodeURIComponent(agentAri)}/details${sourceParam}`,
		email,
	);
};

export const getAtlassianStudioAgentDuplicateUrl = (
	siteId: string,
	agentId: string,
	navigationSource?: string,
	email?: string,
): string => {
	const redirectParam = `redirect=${encodeURIComponent('/create')}`;
	const sourceParam = navigationSource
		? `&navigationSource=${encodeURIComponent(navigationSource)}`
		: '';
	return getStudioPath(
		`/s/${siteId}/agents/enrich/rovo/agents/${agentId}?${redirectParam}${sourceParam}`,
		email,
	);
};

export const getAtlassianStudioAgentPermissionsUrl = (
	siteId: string,
	agentAri: string,
	navigationSource?: string,
	email?: string,
): string => {
	const searchParams = new URLSearchParams();
	if (navigationSource) {
		searchParams.set('navigationSource', encodeURIComponent(navigationSource));
	}
	const query = searchParams.size > 0 ? `?${searchParams}` : '';
	return getStudioPath(
		`/s/${siteId}/agents/${encodeURIComponent(agentAri)}/permissions${query}`,
		email,
	);
};

export const getAtlassianStudioAutomationRuleEditUrl = (
	workspaceId: string,
	spaceId: string,
	ruleIdUuid: string,
	email?: string,
): string =>
	getStudioPath(buildStudioAutomationRuleEditUrl(workspaceId, spaceId, ruleIdUuid), email);
